import type { DestroyRef, Signal, WritableSignal } from '@angular/core';
import { computed, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { FormGroup } from '@angular/forms';
import { queueScheduler, ReplaySubject, Subject } from 'rxjs';
import type { TypedOmit } from '../../shared/interfaces/utils.types';
import { compareObjects } from '../../shared/others/utils';
import type { PalantirModalRef } from '../modal/modal-ref.service';
import type { PalantirFilterModalService } from './filter-modal.service';
import {
  PalantirFilterInvalidBehavior,
  PalantirFilterSaveStrategy,
  PalantirFilterInputType,
  PalantirFilterDependsOnValueEvaluationBehavior,
  PalantirFilterClearFiltersOnDestroyBehavior,
} from './filter-modal.type';
import type {
  PalantirFilterInput,
  PalantirFilterModalDataToFormGroup,
  PalantirFilterModalOnChanges,
  PalantirFilterModalOptions,
  PalantirFilterInputBase,
  PalantirFilterSelectableValue,
  PalantirFilterSelectInput,
  PalantirFilterDateRangeInput,
  PalantirFilterMultiSearchInput,
  PalantirFilterMultiSelectInput,
} from './filter-modal.type';
import { filterModalLogger } from './logger.instance';

const logger = filterModalLogger.extend('instance');

export class PalantirFilterModalInstance<T extends Record<string, PalantirFilterInput>> {
  /**
   * Prefixo para salvar os filtros no local storage
   */
  protected static readonly LOCAL_STORAGE_PREFIX = 'palantir-filter-modal-';

  /**
   * Prefixo para salvar os filtros no local storage
   */
  protected static readonly QUERY_STRING_DIVIDER = '_';

  /**
   * Identificador único do modal
   */
  public readonly id: string;

  /**
   * Metadados do modal, opções iniciais para o modal
   */
  public readonly metadata: PalantirFilterModalOptions<T>;

  /**
   * Grupo de formulários do modal
   */
  public readonly formGroup: PalantirFilterModalDataToFormGroup<T>;

  /**
   * Emite sempre que houver mudanças nos filtros, por ser um BehaviorSubject, sempre emite o último valor ao ser inscrito
   */
  public readonly valueChanges$: ReplaySubject<PalantirFilterModalOnChanges<T>> = new ReplaySubject(
    1,
  );

  /**
   * Ultimo valor emitido por valueChanges$
   */
  public readonly value: WritableSignal<PalantirFilterModalOnChanges<T>> = signal({});

  /**
   * Indica se o modal está filtrando no momento
   */
  // eslint-disable-next-line unicorn/consistent-function-scoping
  public readonly isFiltering: Signal<boolean> = computed(() => this.calculateIsFiltering());

  /**
   * Emite sempre que os filtros forem redefinidos usando o método clearFilters
   */
  public readonly reset$: Subject<void> = new Subject();

  /**
   * Referência de destruição do modal
   */
  protected readonly destroyRef: DestroyRef;

  private resetValue: PalantirFilterModalOnChanges<T> = {} as PalantirFilterModalOnChanges<T>;

  private modalRef: PalantirModalRef | null = null;

  private readonly filterModalService: PalantirFilterModalService;

  public constructor({
    id,
    metadata,
    formGroup,
    destroyRef,
    filterModalService,
  }: {
    destroyRef: DestroyRef;
    filterModalService: PalantirFilterModalService;
    formGroup: PalantirFilterModalDataToFormGroup<T>;
    id: string;
    metadata: PalantirFilterModalOptions<T>;
  }) {
    this.id = id;
    this.metadata = metadata;
    this.formGroup = formGroup;
    this.destroyRef = destroyRef;
    this.filterModalService = filterModalService;

    logger.debug(`Instance "${this.id}" initialized with metadata:`, {
      filters: Object.keys(this.metadata.filters),
      saveStrategy: this.metadata.saveFiltersStrategy,
    });

    this.computeInitialResetValue(this.formGroup);
    this.initListeners();
    this.initFormRestore();
  }

  private initListeners(): void {
    this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      logger.debug(`FormGroup value changed for "${this.id}":`, value);
      this.emitChanges(value as PalantirFilterModalOnChanges<T>);
    });

    this.destroyRef.onDestroy(() => {
      logger.debug(`Destroying instance "${this.id}"`);
      this.destroy();
    });
  }

  private initFormRestore(): void {
    const restored = this.restoreFilters();

    if (!restored) {
      this.valueChanges$.next(this.resetValue);
      this.emitChanges(this.resetValue);
      return;
    }

    this.valueChanges$.next(restored);
  }

  /**
   * @returns O formulário de filtros
   */
  public getFormGroup(): PalantirFilterModalDataToFormGroup<T> {
    return this.formGroup;
  }

  /**
   * Abre o modal com as configurações passadas
   */
  public openModal(): PalantirModalRef {
    this.modalRef = this.filterModalService.openModal(this.id);
    return this.modalRef;
  }

  /**
   * Limpa todos os filtros, redefine o formulário e remove os filtros da query
   */
  public clearFilters(skipEmit = false): void {
    logger.log(`Clearing filters for instance "${this.id}"`, { skipEmit });
    this.formGroup.reset(this.resetValue);

    if (!skipEmit) {
      this.emitChanges(this.resetValue);
      this.reset$.next();
    }

    switch (this.metadata.saveFiltersStrategy!) {
      case PalantirFilterSaveStrategy.QUERY_STRING:
        void this.filterModalService.querifyService.removeParams(this.getQueryKeysToClear());
        break;
      case PalantirFilterSaveStrategy.LOCAL_STORAGE:
        localStorage.removeItem(PalantirFilterModalInstance.LOCAL_STORAGE_PREFIX + this.id);
        break;
      case PalantirFilterSaveStrategy.NONE:
        break;
    }
  }

  /**
   * Fecha o modal caso esteja aberto
   */
  public closeModal(): void {
    void this.modalRef?.close();
    this.modalRef = null;
  }

  /**
   * Destrói o modal e remove da lista de modais. Este método é chamado automaticamente quando o modal é destruído ou quando esse modal for sobrescrito
   */
  public destroy(): void {
    this.valueChanges$.complete();
    this.reset$.complete();

    if (
      this.metadata.clearFiltersOnDestroyBehavior ===
      PalantirFilterClearFiltersOnDestroyBehavior.CLEAR
    ) {
      this.clearFilters(true);
    }

    this.filterModalService.removeDialog(this.id);
  }

  private emitChanges(value: PalantirFilterModalOnChanges<T>): void {
    const finalChanges = this.clearInvalidFilterValues(value);
    logger.debug(`Emitting changes for "${this.id}":`, finalChanges);

    this.value.set(finalChanges);
    // Use queueScheduler to ensure proper emission order after signal update
    queueScheduler.schedule(() => this.valueChanges$.next(finalChanges));

    switch (this.metadata.saveFiltersStrategy!) {
      case PalantirFilterSaveStrategy.QUERY_STRING: {
        void this.filterModalService.querifyService.removeParams(this.getQueryKeysToClear());
        const keyPrefix = `${this.id}${PalantirFilterModalInstance.QUERY_STRING_DIVIDER}`;
        const paramsToSet = Object.fromEntries(
          Object.entries(this.value())
            .filter(
              ([, value]) => value !== null && value !== undefined && String(value).trim().length,
            )
            .map(([key, value]) => [`${keyPrefix}${key}`, value]),
        );
        logger.debug(`Saving to QueryString for "${this.id}":`, paramsToSet);
        void this.filterModalService.querifyService.addParams(paramsToSet);
        break;
      }

      case PalantirFilterSaveStrategy.LOCAL_STORAGE:
        logger.debug(`Saving to LocalStorage for "${this.id}"`);
        localStorage.setItem(
          PalantirFilterModalInstance.LOCAL_STORAGE_PREFIX + this.id,
          JSON.stringify(finalChanges),
        );
        break;
      case PalantirFilterSaveStrategy.NONE:
        break;
    }
  }

  /**
   * Retorna as chaves da query string que devem ser removidas, isso é necessário para limpar os filtros da query string
   */
  private getQueryKeysToClear(): string[] {
    const deflatedParams = Object.keys(this.filterModalService.querifyService.getParams()).filter(
      (key) => key.startsWith(`${this.id}${PalantirFilterModalInstance.QUERY_STRING_DIVIDER}`),
    );

    if (!deflatedParams || Object.keys(deflatedParams).length === 0) {
      return [];
    }

    return deflatedParams;
  }

  /**
   * Calcula o valor inicial de resetValue, que é o valor que o formulário deve ter ao ser limpado ou para ser considerado sem filtros
   */
  private computeInitialResetValue(formGroup: FormGroup): void {
    const resetValue = {} as PalantirFilterModalOnChanges<T>;

    for (const [key, filter] of Object.entries(formGroup.controls)) {
      const definition = this.metadata.filters[key];

      if (!definition) {
        continue;
      }

      switch (definition.type) {
        case PalantirFilterInputType.TEXT:
        case PalantirFilterInputType.SELECT:
        case PalantirFilterInputType.DATE:
        case PalantirFilterInputType.SEARCH:
        case PalantirFilterInputType.NUMBER:
          Reflect.set(resetValue, key, definition.value ?? null);
          break;
        case PalantirFilterInputType.DATE_RANGE:
          Reflect.set(resetValue, key, {
            to: definition.to.value ?? null,
            from: definition.from.value ?? null,
          });
          break;
        case PalantirFilterInputType.MULTI_SEARCH:
        case PalantirFilterInputType.MULTI_SELECT:
          Reflect.set(resetValue, key, definition.value ?? []);
          break;
        case PalantirFilterInputType.CUSTOM:
          Reflect.set(resetValue, key, filter.value ?? null);
          break;
      }
    }

    this.resetValue = resetValue;
  }

  /**
   * Limpa os valores inválidos do filtro, de acordo com a configuração de invalidFormsBehavior
   */
  private clearInvalidFilterValues<E extends Record<string, unknown>>(
    value: E,
    formGroup: FormGroup = this.formGroup,
  ): E {
    if (this.metadata.invalidFormsBehavior === PalantirFilterInvalidBehavior.KEEP) {
      return value;
    }

    const cleanedValue = { ...value };

    for (const [key, filterValue] of Object.entries(cleanedValue)) {
      const control = formGroup.get(key);
      const definition = this.metadata.filters[key];

      if (!control || !definition) {
        continue;
      }

      if (definition && definition.dependsOn) {
        const dependsOnMet = this.dependsOnFilterComputed(definition.dependsOn, cleanedValue);
        if (!dependsOnMet) {
          this.clearFilterValue(cleanedValue, key, definition);
          continue;
        }
      }

      // Check if control is invalid
      if (control.invalid) {
        this.clearFilterValue(cleanedValue, key, definition);
        continue;
      }

      // Validate filter-specific constraints
      if (!this.isValidFilterValue(filterValue, definition)) {
        this.clearFilterValue(cleanedValue, key, definition);
      }
    }

    return cleanedValue;
  }

  /**
   * Validates if a filter value is valid according to its type constraints
   */
  private isValidFilterValue(value: unknown, definition: PalantirFilterInput): boolean {
    if (value === null || value === undefined) {
      return true; // null/undefined are always valid (empty state)
    }

    switch (definition.type) {
      case PalantirFilterInputType.TEXT:
      case PalantirFilterInputType.SEARCH:
        return typeof value === 'string';

      case PalantirFilterInputType.NUMBER:
        return !Number.isNaN(Number(value));

      case PalantirFilterInputType.DATE:
        return value instanceof Date && !Number.isNaN(value.getTime());

      case PalantirFilterInputType.SELECT:
        if (typeof value !== 'string' && typeof value !== 'number') {
          return false;
        }

        return (definition as PalantirFilterSelectInput).options.some((opt) => opt.value === value);

      case PalantirFilterInputType.MULTI_SELECT:
      case PalantirFilterInputType.MULTI_SEARCH: {
        if (!Array.isArray(value)) {
          return false;
        }

        const multiDef = definition as
          | PalantirFilterMultiSearchInput
          | PalantirFilterMultiSelectInput;
        if ('options' in multiDef) {
          const validValues = multiDef.options.map((opt) => opt.value);
          return value.every((val) => validValues.includes(val));
        }

        return value.every((val) => typeof val === 'string' || typeof val === 'number');
      }

      case PalantirFilterInputType.DATE_RANGE: {
        if (typeof value !== 'object' || value === null) {
          return false;
        }

        const dateRange = value as { from?: Date; to?: Date };
        const fromValid =
          !dateRange.from ||
          (dateRange.from instanceof Date && !Number.isNaN(dateRange.from.getTime()));
        const toValid =
          !dateRange.to || (dateRange.to instanceof Date && !Number.isNaN(dateRange.to.getTime()));
        return fromValid && toValid;
      }

      case PalantirFilterInputType.CUSTOM:
        return true; // Custom filters handle their own validation

      default:
        return true;
    }
  }

  /**
   * Clears a filter value by setting it to its reset state
   */
  private clearFilterValue<E extends Record<string, unknown>>(
    cleanedValue: E,
    key: string,
    definition: PalantirFilterInput,
  ): void {
    switch (definition.type) {
      case PalantirFilterInputType.TEXT:
      case PalantirFilterInputType.SELECT:
      case PalantirFilterInputType.DATE:
      case PalantirFilterInputType.SEARCH:
      case PalantirFilterInputType.NUMBER:
        Reflect.set(cleanedValue, key, definition.value || null);
        break;

      case PalantirFilterInputType.DATE_RANGE: {
        const dateRangeDef = definition as PalantirFilterDateRangeInput;
        Reflect.set(cleanedValue, key, {
          to: dateRangeDef.to.value ?? null,
          from: dateRangeDef.from.value ?? null,
        });
        break;
      }

      case PalantirFilterInputType.MULTI_SEARCH:
      case PalantirFilterInputType.MULTI_SELECT:
        Reflect.set(cleanedValue, key, definition.value ?? []);
        break;
      case PalantirFilterInputType.CUSTOM:
        Reflect.set(cleanedValue, key, null);
        break;
    }
  }

  /**
   * Computa se o modal está filtrando no momento, comparando o valor atual com o valor inicial
   */
  private calculateIsFiltering(): boolean {
    return !compareObjects(this.value(), this.resetValue);
  }

  /**
   * Restaura os filtros salvos de acordo com a configuração de saveFiltersStrategy
   */
  private restoreFilters(): PalantirFilterModalOnChanges<T> | null {
    switch (this.metadata.saveFiltersStrategy!) {
      case PalantirFilterSaveStrategy.QUERY_STRING: {
        const keyPrefix = `${this.id}${PalantirFilterModalInstance.QUERY_STRING_DIVIDER}`;
        const queryFilters = this.filterModalService.querifyService.getParams();

        if (typeof queryFilters !== 'object') {
          return false;
        }

        const queryKeys = Object.keys(queryFilters).filter((key) => key.startsWith(keyPrefix));

        if (queryKeys.length === 0) {
          return false;
        }

        const filteredQuery = Object.fromEntries(
          queryKeys
            .map((key) => {
              const filterKey = key.replace(keyPrefix, '');
              const rawValue = queryFilters[key];
              const definition = this.metadata.filters[filterKey];
              return [filterKey, this.parseQueryValue(rawValue, definition)];
            })
            .filter(([key]) => this.metadata.filters[key as keyof T]), // Remove chaves que não existem no metadata.filters
        );

        const cleanedValues = this.clearInvalidFilterValues(
          filteredQuery as PalantirFilterModalOnChanges<T>,
        );

        logger.info(`Restored filters from QueryString for "${this.id}":`, cleanedValues);

        this.formGroup.patchValue(cleanedValues);
        return cleanedValues;
      }

      case PalantirFilterSaveStrategy.LOCAL_STORAGE: {
        const localStorageFilters = localStorage.getItem(
          PalantirFilterModalInstance.LOCAL_STORAGE_PREFIX + this.id,
        );

        if (!localStorageFilters) {
          return false;
        }

        try {
          const parsed = JSON.parse(localStorageFilters);

          const cleanedValues = this.clearInvalidFilterValues(
            parsed as PalantirFilterModalOnChanges<T>,
          );

          logger.info(`Restored filters from LocalStorage for "${this.id}":`, cleanedValues);

          this.formGroup.patchValue(cleanedValues);
          return cleanedValues;
        } catch (error) {
          logger.error(
            `Failed to restore filters from localStorage for modal "${this.id}":`,
            error,
          );
          localStorage.removeItem(PalantirFilterModalInstance.LOCAL_STORAGE_PREFIX + this.id);
          return false;
        }
      }

      case PalantirFilterSaveStrategy.NONE:
        return null;
    }

    return false;
  }

  /**
   * Parses a query parameter value based on filter type
   */
  private parseQueryValue(value: string | unknown, definition?: PalantirFilterInput): unknown {
    if (!definition || value === null || value === undefined) {
      return value;
    }

    try {
      switch (definition.type) {
        case PalantirFilterInputType.TEXT:
        case PalantirFilterInputType.SEARCH:
          return value || null;
        case PalantirFilterInputType.NUMBER: {
          if (value === '' || value === null || value === undefined) {
            return null;
          }

          const parsed = Number(value);
          return Number.isNaN(parsed) ? null : parsed;
        }

        case PalantirFilterInputType.DATE: {
          if (!value || value === '' || value === null || value === undefined) {
            return null;
          }

          const parsed = new Date(value as string);
          return Number.isNaN(parsed.getTime()) ? null : parsed;
        }

        case PalantirFilterInputType.SELECT: {
          if (!value || value === '' || value === null || value === undefined) {
            return null;
          }

          const selectDef = definition as PalantirFilterSelectInput;
          const matchedOption = selectDef.options.find((opt) => String(opt.value) === value);
          return matchedOption ? matchedOption.value : null;
        }

        case PalantirFilterInputType.MULTI_SELECT:
        case PalantirFilterInputType.MULTI_SEARCH:
          return Array.isArray(value) ? value : [];
        case PalantirFilterInputType.DATE_RANGE: {
          if (!value || typeof value !== 'object') {
            return null;
          }

          const from = 'from' in value ? new Date(value.from as string) : (null as Date | null);
          const to = 'to' in value ? new Date(value.to as string) : (null as Date | null);

          return {
            from: Number.isNaN(from?.getTime() ?? Number.NaN) ? null : from,
            to: Number.isNaN(to?.getTime() ?? Number.NaN) ? null : to,
          };
        }

        case PalantirFilterInputType.CUSTOM:
          return value;
        default:
          return value;
      }
    } catch {
      return value;
    }
  }

  /**
   * @internal
   */
  public dependsOnFilterComputed(
    dependsOn: PalantirFilterInputBase['dependsOn'],
    _value?: Record<string, unknown>,
  ): boolean {
    if (!dependsOn) {
      return true;
    }

    if (Object.keys(this.value()).length === 0) {
      return false;
    }

    const effectiveValue = _value ?? this.value();

    const filterValue = effectiveValue[dependsOn.filter];
    const filterControl = this.formGroup.get(dependsOn.filter);

    if (!filterControl || filterValue === undefined) {
      logger.warn(
        `Filter ${dependsOn.filter} not found in filter value or formGroup, please check if the filter exists`,
      );
      return false;
    }

    if (filterControl.disabled || filterControl.invalid) {
      return false;
    }

    const hasValue = Array.isArray(filterValue) ? filterValue.length > 0 : Boolean(filterValue);

    if (!hasValue) {
      return false;
    }

    return dependsOn.value === undefined
      ? true
      : this.evaluateDependsOnFilterValue(filterValue as PalantirFilterSelectableValue, {
          ...dependsOn,
          value: dependsOn.value,
        });
  }

  private evaluateDependsOnFilterValue(
    filterValue: PalantirFilterSelectableValue | PalantirFilterSelectableValue[],
    dependsOn: TypedOmit<NonNullable<PalantirFilterInputBase['dependsOn']>, 'value'> & {
      value: PalantirFilterSelectableValue | PalantirFilterSelectableValue[];
    },
  ): boolean {
    if (
      dependsOn.valueEvaluationBehavior ===
        PalantirFilterDependsOnValueEvaluationBehavior.ALL_CONTAINS &&
      !Array.isArray(filterValue)
    ) {
      logger.warn(
        `Filter ${dependsOn.filter} has an invalid valueEvaluationBehavior, it should be ALL_CONTAINS but the filter is not a multi choice filter`,
      );
      return false;
    }

    const evaluationBehavior =
      dependsOn.valueEvaluationBehavior ??
      PalantirFilterDependsOnValueEvaluationBehavior.SOME_CONTAINS_OR_EQUAL;

    const normalizedValues = Array.isArray(dependsOn.value) ? dependsOn.value : [dependsOn.value];

    switch (evaluationBehavior) {
      case PalantirFilterDependsOnValueEvaluationBehavior.SOME_CONTAINS_OR_EQUAL:
        return Array.isArray(filterValue)
          ? filterValue.some((value) => normalizedValues.includes(value as string))
          : normalizedValues.includes(filterValue as string);

      case PalantirFilterDependsOnValueEvaluationBehavior.ALL_CONTAINS:
        return normalizedValues.every((value) =>
          (filterValue as PalantirFilterSelectableValue[]).includes(value),
        );

      case PalantirFilterDependsOnValueEvaluationBehavior.NONE_CONTAINS_OR_NOT_EQUAL:
        return Array.isArray(filterValue)
          ? !filterValue.some((value) => normalizedValues.includes(value as string))
          : !normalizedValues.includes(filterValue as string);

      default:
        logger.warn(
          `Filter ${dependsOn.filter} has an invalid valueEvaluationBehavior, it should be one of ${Object.values(
            PalantirFilterDependsOnValueEvaluationBehavior,
          )}`,
        );
        return false;
    }
  }
}
